import { JSONSchema6, JSONSchema6TypeName } from "json-schema";
import Ajv, { ValidateFunction } from "ajv";

/**
 * Custom field schema types and the AJV validation that goes with them.
 *
 * This lives apart from CustomFieldsV2 on purpose: it is pure logic with no React,
 * MUI or redux behind it, so it can be unit tested on its own and reused by any form.
 * CustomFieldsV2 re-exports everything here, so existing imports keep working.
 */

export type ExtendedTypes = JSONSchema6TypeName | JSONSchema6TypeName[] | undefined | "text";

/** Tidy-up rules a field can ask for before its value is saved. */
export type CustomFieldSanitizer = "email" | "emailList";

type OCFields =
  | "UnitValue"
  | "Dropdown"
  | "Date"
  | "Year"
  | "OC_CrewMembers"
  | "OC_Airports"
  | "OC_CrewPositions"
  | "OC_CrewGroups"
  | "OC_CrewBases"
  | "OC_Aircraft"
  | "OC_AircraftTypes"
  | "OC_AircraftGroups"
  | "OC_Hotels"
  | "OC_NonFlyingTypes"
  | "OC_FlightTypes"
  | "OC_Calculated"
  | "OC_Customers"
  | "OC_DelayCodes"
  | "OC_DelayCategories"
  | "OC_ATA"
  | "OC_FlightFlags"
  | "OC_LogicModifiers";

export interface IExtendedJSONSchema6Definition extends Omit<JSONSchema6, "type" | "properties"> {
  hidden?: boolean;
  type?: ExtendedTypes;
  /** Nested custom fields, e.g. a UnitValue's value/unit pair, or a grouped object. */
  properties?: { [key: string]: IExtendedJSONSchema6Definition };
  objectType?: OCFields;
  readOnly?: boolean;
  placeholder?: string;
  selectorProps?: any;
  multiline?: boolean;
  rows?: number;
  /**
   * User facing message shown instead of AJV's raw validation error. Either one
   * message for the whole field, or one per rule, for example:
   * { pattern: "Expected format: example@domain.com", maxLength: "Too long" }
   * `default` is used for any rule that has no message of its own.
   */
  errorMessage?: string | { [keyword: string]: string };
  /** Tidy the value before it is saved. See `sanitizeCustomValues`. */
  sanitize?: CustomFieldSanitizer;
}

/** Top-level schema type whose properties are ExtendedJSONSchema6Definition entries */
export interface IExtendedJSONSchema6 extends Omit<JSONSchema6, "properties" | "required"> {
  properties?: { [key: string]: IExtendedJSONSchema6Definition };
  required?: string[];
}

/**
 * One compiled validator per schema object. Compiling is expensive (AJV writes and
 * builds a function), so it must not happen on every keystroke. Using a fresh Ajv
 * per schema also avoids "schema with key or id already exists" if the same $id is
 * parsed again.
 */
const validatorCache = new WeakMap<object, ValidateFunction | null>();

const getValidator = (schema: IExtendedJSONSchema6): ValidateFunction | null => {
  const cached = validatorCache.get(schema);
  if (cached !== undefined) return cached;

  let validate: ValidateFunction | null = null;
  try {
    validate = new Ajv({ allErrors: true }).compile(schema);
  } catch (error) {
    // The schema is hand edited JSON in the tenant database. A bad regex in it
    // must not take the whole form down.
    console.error("Custom fields: unable to compile schema.", error);
  }
  validatorCache.set(schema, validate);
  return validate;
};

/** Walk a dotted path such as "address.city" or "weight.value" down the schema. */
const getFieldDefinition = (
  schema: IExtendedJSONSchema6 | null,
  fieldPath: string
): IExtendedJSONSchema6Definition | undefined => {
  if (!schema || !fieldPath) return undefined;
  let current: any = schema;
  for (const key of fieldPath.split(".")) {
    current = current?.properties?.[key];
    if (!current) return undefined;
  }
  return current as IExtendedJSONSchema6Definition;
};

/**
 * AJV's raw text for these rules is unreadable for a user (a "pattern" failure prints
 * the whole regex), so we never show it even when the field has no message of its own.
 */
const rawMessageFallbacks: Record<string, string> = {
  pattern: "Invalid format",
};

/**
 * The message to show the user: the field's message for this rule, then the field's
 * general message, then a readable fallback, then AJV's raw text.
 */
const getFieldErrorMessage = (
  fieldDef: IExtendedJSONSchema6Definition | undefined,
  keyword: string,
  rawMessage?: string
): string => {
  const custom = fieldDef?.errorMessage;
  if (typeof custom === "string") return custom;
  const perKeyword = custom ? (custom[keyword] ?? custom.default) : undefined;
  return perKeyword ?? rawMessageFallbacks[keyword] ?? rawMessage ?? "Invalid value";
};

const SANITIZERS: Record<CustomFieldSanitizer, (value: string) => string> = {
  /** A single address: drop the whitespace around it. */
  email: (value) => value.trim(),
  /**
   * A ;-separated list: drop the whitespace around each address and any empty entries.
   * The stored value is always "a@x.com;b@y.com", whatever the user typed.
   */
  emailList: (value) =>
    value
      .split(";")
      .map((address) => address.trim())
      .filter(Boolean)
      .join(";"),
};

/**
 * Tidy custom values before they are saved. The email patterns accept spaces and line
 * breaks so a long list can be typed readably, but the send side splits on ";" without
 * cleaning up (Vmo.OC.Backend ToEmail.ts), so a stored line break would become part of
 * an address and fail silently. Tidying on save keeps what is stored exactly as clean
 * as it was before, while letting people type it however they like.
 *
 * Returns a new object. Values with no `sanitize` rule are passed through untouched.
 */
export const sanitizeCustomValues = (schema: IExtendedJSONSchema6 | null, customValues: any): any => {
  if (!schema || !customValues || typeof customValues !== "object" || Array.isArray(customValues)) {
    return customValues;
  }

  const walk = (properties: { [key: string]: IExtendedJSONSchema6Definition } | undefined, values: any): any => {
    if (!properties || !values || typeof values !== "object" || Array.isArray(values)) return values;

    const result: Record<string, any> = { ...values };
    Object.keys(values).forEach((key) => {
      const field = properties[key];
      if (!field) return;

      if (field.properties) {
        result[key] = walk(field.properties, values[key]);
        return;
      }
      const sanitizer = field.sanitize ? SANITIZERS[field.sanitize] : undefined;
      if (sanitizer && typeof values[key] === "string") result[key] = sanitizer(values[key]);
    });
    return result;
  };

  return walk(schema.properties, customValues);
};

export interface IComputeCustomFieldErrorsOptions {
  /**
   * AJV rules to leave out of the result. CustomerForm passes ["required"] so that a
   * missing hidden or read only field can never lock the save button.
   */
  ignoreKeywords?: string[];
}

/**
 * Pure AJV validation of custom field values. Returns a FLAT map keyed by full
 * Formik path, e.g. { "custom.emails": "Expected format: example@domain.com" }.
 * Used both for showing errors and by CustomerForm's Formik `validate`, so custom
 * field errors survive submit.
 */
export const computeCustomFieldErrors = (
  schema: IExtendedJSONSchema6 | null,
  customValues: any,
  propertyPath = "",
  options: IComputeCustomFieldErrorsOptions = {}
): Record<string, string> => {
  const validate = schema ? getValidator(schema) : null;
  if (!schema || !validate) return {};

  const requiredFields = schema.required ?? [];
  const unitValueFields = Object.entries(schema.properties ?? {})
    .filter(([, value]) => value?.objectType === "UnitValue")
    .map(([key]) => key);

  // AJV's `required` rule looks for the key itself, so leave empty values out
  // completely rather than sending "" or null.
  const fixedValues: Record<string, any> = {};
  for (const [key, value] of Object.entries(customValues ?? {})) {
    if (value !== null && value !== "" && value !== undefined) fixedValues[key] = value;
  }

  const errors: Record<string, string> = {};
  if (validate(fixedValues)) return errors;

  validate.errors?.forEach((error: any) => {
    const { params, message, keyword } = error;
    if (options.ignoreKeywords?.includes(keyword)) return;

    const dataPath: string = (error?.dataPath || error?.instancePath || "") as string;
    const missingProp = params?.missingProperty as string | undefined;

    // Where the field sits in the schema, e.g. "emails" or "address.city".
    const fieldPath = [dataPath.replace(/^\./, ""), missingProp].filter(Boolean).join(".");
    const isUnitValue = !!missingProp && unitValueFields.includes(missingProp);

    // Full Formik path. Joining the parts means we can never build ".." or a stray
    // dot, so propertyPath works with or without a trailing dot.
    const errorPath = [propertyPath.replace(/\.$/, ""), fieldPath, isUnitValue ? "value" : ""]
      .filter(Boolean)
      .join(".");

    // For UnitValue fields that are not required, ignore null integers (parity with V1).
    const parentProperty = fieldPath.split(".")[0];
    const isNullInteger = message === "should be integer";
    if (unitValueFields.includes(parentProperty) && !requiredFields.includes(parentProperty) && isNullInteger) {
      return;
    }

    errors[errorPath] = missingProp
      ? "required"
      : getFieldErrorMessage(getFieldDefinition(schema, fieldPath), keyword, message);
  });

  return errors;
};
